import { STEP_IDS, type StepId, type WizardState } from './types';

/* ── Step index helpers ─────────────────────────────── */

export function stepIndex(stepId: StepId): number {
  return STEP_IDS.indexOf(stepId);
}

export function totalSteps(): number {
  return STEP_IDS.length;
}

/* ── Navigation selectors ───────────────────────────── */

export function canGoBack(state: WizardState): boolean {
  return stepIndex(state.currentStep) > 0;
}

export function previousStep(state: WizardState): StepId | null {
  const idx = stepIndex(state.currentStep);
  return idx > 0 ? STEP_IDS[idx - 1] : null;
}

export function nextStep(state: WizardState): StepId | null {
  const idx = stepIndex(state.currentStep);
  return idx < STEP_IDS.length - 1 ? STEP_IDS[idx + 1] : null;
}

export function isLastStep(state: WizardState): boolean {
  return stepIndex(state.currentStep) === STEP_IDS.length - 1;
}

/* ── Step validation ────────────────────────────────── */

export function isStepValid(state: WizardState, stepId: StepId): boolean {
  const { draft } = state;
  switch (stepId) {
    case 'place':
      return !!draft.selectedPlace?.id;
    case 'name':
      return draft.producerName.trim().length >= 2;
    case 'offerings':
      return true; // offerings are optional
    default:
      return false;
  }
}

/** Minimal email check */
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function isEmailValid(email: string): boolean {
  return EMAIL_RE.test(email.trim());
}

export function canGoNext(state: WizardState): boolean {
  if (!isStepValid(state, state.currentStep)) return false;
  // On last step, also require valid contact email
  if (isLastStep(state) && !isEmailValid(state.draft.contactEmail)) return false;
  return true;
}

/* ── CTA label ──────────────────────────────────────── */

export function ctaLabel(state: WizardState): string {
  return isLastStep(state) ? 'Skicka intresseanmälan' : 'Fortsätt';
}

/* ── Inline validation hint ─────────────────────────── */

export function validationHint(state: WizardState): string | null {
  if (canGoNext(state)) return null;
  switch (state.currentStep) {
    case 'place':
      return 'Välj en plats för att fortsätta.';
    case 'name':
      return 'Skriv namnet på din verksamhet.';
    case 'offerings':
      if (isLastStep(state) && !isEmailValid(state.draft.contactEmail)) {
        return 'Ange en giltig e-postadress.';
      }
      return null;
    default:
      return null;
  }
}
